import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

const SESSION_KEY = 'hjb_visitor_session';

function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

function getOrCreateSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

// Helper to schedule work when browser is idle
function scheduleIdleTask(callback: () => void, timeout = 3000) {
  if ('requestIdleCallback' in window) {
    (window as Window).requestIdleCallback(callback, { timeout });
  } else {
    // Fallback for Safari - use longer timeout to not compete with LCP
    setTimeout(callback, timeout);
  }
}

export function useVisitorTracking() {
  const location = useLocation();
  const hasTrackedRef = useRef(false);
  const lastPathRef = useRef<string>('');

  useEffect(() => {
    const trackVisitor = async () => {
      try {
        const sessionId = getOrCreateSessionId();
        const isFirstVisit = !hasTrackedRef.current;
        
        // Only track if path changed
        if (location.pathname === lastPathRef.current && !isFirstVisit) {
          return;
        }
        
        lastPathRef.current = location.pathname;

        const payload = {
          session_id: sessionId,
          landing_page: location.pathname,
          referrer: isFirstVisit ? document.referrer : undefined,
          user_agent: isFirstVisit ? navigator.userAgent : undefined,
        };

        // Call edge function
        const { error } = await supabase.functions.invoke('track-visitor', {
          body: payload,
        });

        if (error) {
          console.error('Visitor tracking error:', error);
        }

        hasTrackedRef.current = true;
      } catch (err) {
        // Silently fail - tracking should never break the app
        console.error('Visitor tracking failed:', err);
      }
    };

    // Schedule tracking during idle time to not block initial render/LCP
    scheduleIdleTask(trackVisitor, 3000);
  }, [location.pathname]);
}

// Export session ID getter for use in registration tracking
export function getVisitorSessionId(): string {
  return getOrCreateSessionId();
}
